import type { AnswerKey, ErrorType, Evaluation, Question, QuestionSnapshot, ResponseValue } from '@/core/types';
import { fold, levenshtein, looseAnswer, normalizeAnswer } from '@/core/util/misc';
import { parseNumberAnswer } from './expr';
import { VALIDATORS } from './validators';
import { L } from '@/i18n';

/** Evaluate any response against its answer key. Pure function. */
export function evaluate(q: Question | QuestionSnapshot, r: ResponseValue | null): Evaluation {
  const expected = describeAnswer(q);
  if (!r) return { correct: false, score: 0, feedback: L('No answer given.', 'Du har ikke svart.'), expected, errorType: 'incomplete' };
  const a = q.answer;
  const mis = (key?: string) => (key ? (q as Question).misconceptionMap?.[key] : undefined);

  switch (a.type) {
    case 'choice': {
      if (r.type !== 'choice') break;
      const ok = r.value === a.correct;
      return { correct: ok, score: ok ? 1 : 0, feedback: ok ? L('Correct.', 'Riktig.') : L('Not this one.', 'Ikke denne.'), expected, misconceptionId: ok ? undefined : mis(r.value), errorType: ok ? undefined : mis(r.value) ? 'conceptual' : 'unknown' };
    }
    case 'multi': {
      if (r.type !== 'multi') break;
      const want = new Set(a.correct);
      const got = new Set(r.value);
      const hits = [...got].filter((x) => want.has(x)).length;
      const wrong = [...got].filter((x) => !want.has(x));
      const ok = hits === want.size && wrong.length === 0;
      const score = Math.max(0, (hits - wrong.length) / want.size);
      return { correct: ok, score, feedback: ok ? L('All correct.', 'Alt riktig.') : L(`${hits} of ${want.size} correct${wrong.length ? `, ${wrong.length} incorrect selected` : ''}.`, `${hits} av ${want.size} riktige${wrong.length ? `, ${wrong.length} feil valgt` : ''}.`), expected, misconceptionId: wrong.map((w) => mis(w)).find(Boolean), errorType: ok ? undefined : hits ? 'incomplete' : 'conceptual' };
    }
    case 'numeric': {
      if (r.type !== 'numeric' && r.type !== 'text') break;
      const v = parseNumberAnswer(r.value);
      if (v === null) return { correct: false, score: 0, feedback: L('That is not a number I can read. Use digits, a decimal point/comma or a fraction like 3/4.', 'Det tallet kan jeg ikke lese. Bruk sifre, desimalkomma eller en brøk som 3/4.'), expected, errorType: 'misread' };
      const ok = Math.abs(v - a.value) <= Math.max(a.tolerance, 1e-9);
      let errorType: ErrorType | undefined;
      let feedback = ok ? L('Correct.', 'Riktig.') : L('Not quite.', 'Ikke helt.');
      if (!ok) {
        if (Math.abs(v + a.value) <= Math.max(a.tolerance, 1e-9) && a.value !== 0) { errorType = 'sign'; feedback = L('The size is right but the sign is wrong.', 'Tallverdien er riktig, men fortegnet er feil.'); }
        else if (a.value !== 0 && [10, 100, 1000, 0.1, 0.01, 0.001].some((f) => Math.abs(v - a.value * f) <= Math.abs(a.value * f) * 1e-6)) { errorType = 'computational'; feedback = L('Off by a power of ten — check place value / units.', 'Feil med en tierpotens — sjekk plassverdi og enheter.'); }
        else if (a.value !== 0 && Math.abs(v - a.value) / Math.abs(a.value) < 0.05) { errorType = 'computational'; feedback = L('Very close — check rounding or a calculation step.', 'Veldig nær — sjekk avrunding eller et regnesteg.'); }
        else errorType = 'procedural';
      }
      const key = normalizeAnswer(r.value);
      return { correct: ok, score: ok ? 1 : 0, feedback, expected, errorType, misconceptionId: ok ? undefined : mis(key) ?? mis(String(v)) };
    }
    case 'text': {
      if (r.type !== 'text' && r.type !== 'numeric') break;
      const val = r.value;
      const exact = a.accepted.some((acc) => normalizeAnswer(acc, a.caseSensitive) === normalizeAnswer(val, a.caseSensitive));
      if (exact) return { correct: true, score: 1, feedback: 'Correct.', expected };
      const loose = a.accepted.some((acc) => looseAnswer(acc) === looseAnswer(val));
      if (loose) return { correct: true, score: 0.85, feedback: L('Correct — but check accents/special letters.', 'Riktig — men sjekk aksenter og spesialbokstaver.'), expected, errorType: 'spelling' };
      const close = a.accepted.some((acc) => {
        const x = looseAnswer(acc);
        return x.length >= 5 && levenshtein(x, looseAnswer(val)) <= 1;
      });
      if (close) return { correct: false, score: 0.4, feedback: L('Almost — one letter is off.', 'Nesten — én bokstav er feil.'), expected, errorType: 'spelling' };
      return { correct: false, score: 0, feedback: L('Not correct.', 'Ikke riktig.'), expected, misconceptionId: mis(normalizeAnswer(val)), errorType: 'unknown' };
    }
    case 'match': {
      if (r.type !== 'match') break;
      const keys = Object.keys(a.pairs);
      const right = keys.filter((k) => r.value[k] === a.pairs[k]).length;
      const ok = right === keys.length;
      const parts = Object.fromEntries(keys.map((k) => [k, r.value[k] === a.pairs[k]]));
      return { correct: ok, score: right / keys.length, feedback: ok ? L('All pairs correct.', 'Alle parene er riktige.') : L(`${right} of ${keys.length} pairs correct.`, `${right} av ${keys.length} par er riktige.`), expected, parts, errorType: ok ? undefined : 'conceptual' };
    }
    case 'order': {
      if (r.type !== 'order') break;
      const n = a.sequence.length;
      const pos = a.sequence.filter((id, i) => r.value[i] === id).length;
      const ok = pos === n;
      return { correct: ok, score: pos / n, feedback: ok ? L('Correct order.', 'Riktig rekkefølge.') : L(`${pos} of ${n} in the right position.`, `${pos} av ${n} står på riktig plass.`), expected, errorType: ok ? undefined : 'reasoning' };
    }
    case 'categorize': {
      if (r.type !== 'categorize') break;
      const keys = Object.keys(a.assignment);
      const right = keys.filter((k) => r.value[k] === a.assignment[k]).length;
      const ok = right === keys.length;
      const parts = Object.fromEntries(keys.map((k) => [k, r.value[k] === a.assignment[k]]));
      return { correct: ok, score: right / keys.length, feedback: ok ? L('Everything sorted correctly.', 'Alt er sortert riktig.') : L(`${right} of ${keys.length} placed correctly.`, `${right} av ${keys.length} er plassert riktig.`), expected, parts, errorType: ok ? undefined : 'conceptual' };
    }
    case 'steps': {
      if (r.type !== 'steps') break;
      const keys = Object.keys(a.values);
      const parts: Record<string, boolean> = {};
      for (const k of keys) {
        const want = a.values[k];
        const got = r.value[k] ?? '';
        if (want.value !== undefined) {
          const v = parseNumberAnswer(got);
          parts[k] = v !== null && Math.abs(v - want.value) <= Math.max(want.tolerance ?? 1e-6, 1e-9);
        } else {
          parts[k] = (want.accepted ?? []).some((acc) => normalizeAnswer(acc) === normalizeAnswer(got));
        }
      }
      const right = Object.values(parts).filter(Boolean).length;
      const ok = right === keys.length;
      return { correct: ok, score: right / keys.length, feedback: ok ? L('Every step correct.', 'Alle stegene er riktige.') : L(`${right} of ${keys.length} steps correct.`, `${right} av ${keys.length} steg er riktige.`), expected, parts, errorType: ok ? undefined : right ? 'procedural' : 'conceptual' };
    }
    case 'tokens': {
      if (r.type !== 'tokens') break;
      const spec = q.response.type === 'tokens' ? q.response.tokens : [];
      const sentence = r.value.map((id) => spec.find((t) => t.id === id)?.label ?? '').join(' ');
      const ok = a.sentences.some((s) => normalizeAnswer(s) === normalizeAnswer(sentence));
      return { correct: ok, score: ok ? 1 : 0, feedback: ok ? L('Correct sentence.', 'Riktig setning.') : L('The word order is not right yet.', 'Ordstillingen er ikke riktig ennå.'), expected, errorType: ok ? undefined : 'grammar' };
    }
    case 'tf-reason': {
      if (r.type !== 'tf-reason') break;
      const tOk = r.truth === a.truth;
      const rOk = r.reason === a.reason;
      const ok = tOk && rOk;
      return { correct: ok, score: (tOk ? 0.5 : 0) + (rOk ? 0.5 : 0), feedback: ok ? L('Correct verdict and reasoning.', 'Riktig svar og riktig begrunnelse.') : tOk ? L('Right verdict, but the reason is not the best one.', 'Riktig svar, men begrunnelsen er ikke den beste.') : L('The verdict is not right.', 'Svaret er ikke riktig.'), expected, misconceptionId: rOk ? undefined : mis(r.reason ?? ''), errorType: ok ? undefined : 'reasoning' };
    }
    case 'pair': {
      if (r.type !== 'pair') break;
      const f = r.first === a.first;
      const s = r.second === a.second;
      return { correct: f && s, score: (f ? 0.5 : 0) + (s ? 0.5 : 0), feedback: f && s ? L('Both correct.', 'Begge er riktige.') : L('One or both choices are wrong.', 'Ett eller begge valgene er feil.'), expected, errorType: f && s ? undefined : 'conceptual' };
    }
    case 'rubric': {
      if (r.type !== 'text') break;
      return evaluateRubric(a, r.value, expected);
    }
    case 'validator': {
      if (r.type !== 'text' && r.type !== 'numeric') break;
      const v = VALIDATORS[a.validatorId];
      if (!v) return { correct: false, score: 0, feedback: L('Validator unavailable.', 'Kontrollen er ikke tilgjengelig.'), expected };
      const res = v(r.value, a.params);
      return { correct: res.ok, score: res.ok ? 1 : 0, feedback: res.feedback, expected, errorType: res.ok ? undefined : 'procedural' };
    }
  }
  return { correct: false, score: 0, feedback: L('Answer format did not match the question.', 'Svarformatet passet ikke til oppgaven.'), expected, errorType: 'misread' };
}

/** Local keyword rubric — transparent, not AI. */
export function evaluateRubric(a: Extract<AnswerKey, { type: 'rubric' }>, textValue: string, expected: string): Evaluation {
  const t = ` ${fold(textValue)} `;
  const words = textValue.trim().split(/\s+/).filter(Boolean).length;
  const total = a.criteria.reduce((s, c) => s + c.weight, 0);
  const parts: Record<string, boolean> = {};
  let got = 0;
  for (const c of a.criteria) {
    const hit = c.keywords.some((k) => t.includes(fold(k)));
    parts[c.id] = hit;
    if (hit) got += c.weight;
  }
  let score = total ? got / total : 0;
  if (words < 8) score *= 0.5;
  const ok = score >= a.minScore;
  const missing = a.criteria.filter((c) => !parts[c.id]).map((c) => c.label.toLowerCase());
  return {
    correct: ok,
    score,
    feedback: ok
      ? L(`Your answer covers the key elements${missing.length ? ` (could still add: ${missing.join(', ')})` : ''}. Checked with a local keyword rubric, not AI.`, `Svaret ditt dekker hovedpunktene${missing.length ? ` (du kan fortsatt ta med: ${missing.join(', ')})` : ''}. Sjekket med en lokal nøkkelordliste, ikke KI.`)
      : L(`Missing: ${missing.join(', ')}. Checked with a local keyword rubric, not AI.`, `Mangler: ${missing.join(', ')}. Sjekket med en lokal nøkkelordliste, ikke KI.`),
    expected,
    parts,
    errorType: ok ? undefined : 'incomplete',
  };
}

export function describeAnswer(q: Question | QuestionSnapshot): string {
  const a = q.answer;
  const label = (id: string) => {
    const r = q.response;
    if (r.type === 'choice' || r.type === 'multi') return r.options.find((o) => o.id === id)?.label ?? id;
    if (r.type === 'tf-reason') return r.reasons.find((o) => o.id === id)?.label ?? id;
    return id;
  };
  switch (a.type) {
    case 'choice': return label(a.correct);
    case 'multi': return a.correct.map(label).join('; ');
    case 'numeric': return a.display ?? String(a.value);
    case 'text': return a.display ?? a.accepted[0];
    case 'match': {
      const r = q.response.type === 'match' ? q.response : null;
      return Object.entries(a.pairs).map(([l, rr]) => `${r?.left.find((x) => x.id === l)?.label} → ${r?.right.find((x) => x.id === rr)?.label}`).join('; ');
    }
    case 'order': {
      const r = q.response.type === 'order' ? q.response : null;
      return a.sequence.map((id) => r?.items.find((x) => x.id === id)?.label).join(' → ');
    }
    case 'categorize': {
      const r = q.response.type === 'categorize' ? q.response : null;
      return Object.entries(a.assignment).map(([i, c]) => `${r?.items.find((x) => x.id === i)?.label}: ${r?.categories.find((x) => x.id === c)?.label}`).join('; ');
    }
    case 'steps': {
      const r = q.response.type === 'steps' ? q.response : null;
      return Object.entries(a.values).map(([k, v]) => `${r?.steps.find((s) => s.id === k)?.label}: ${v.value ?? v.accepted?.[0]}`).join('; ');
    }
    case 'tokens': return a.sentences[0];
    case 'tf-reason': return `${a.truth ? L('True', 'Sant') : L('False', 'Usant')} — ${label(a.reason)}`;
    case 'pair': {
      const r = q.response.type === 'pair' ? q.response : null;
      return `${r?.firstLabel}: ${r?.first.find((x) => x.id === a.first)?.label}; ${r?.secondLabel}: ${r?.second.find((x) => x.id === a.second)?.label}`;
    }
    case 'rubric': return a.exemplar;
    case 'validator': return a.display;
  }
}

/** Human-readable version of a response (for the Mistake Bank). */
export function describeResponse(q: Question | QuestionSnapshot, r: ResponseValue | null): string {
  if (!r) return L('(no answer)', '(ikke svart)');
  const resp = q.response;
  switch (r.type) {
    case 'choice': return resp.type === 'choice' ? resp.options.find((o) => o.id === r.value)?.label ?? r.value : r.value;
    case 'multi': return resp.type === 'multi' ? r.value.map((v) => resp.options.find((o) => o.id === v)?.label ?? v).join('; ') : r.value.join('; ');
    case 'numeric':
    case 'text': return r.value;
    case 'match': return resp.type === 'match' ? Object.entries(r.value).map(([l, rr]) => `${resp.left.find((x) => x.id === l)?.label} → ${resp.right.find((x) => x.id === rr)?.label}`).join('; ') : '';
    case 'order': return resp.type === 'order' ? r.value.map((id) => resp.items.find((x) => x.id === id)?.label).join(' → ') : '';
    case 'categorize': return resp.type === 'categorize' ? Object.entries(r.value).map(([i, c]) => `${resp.items.find((x) => x.id === i)?.label}: ${resp.categories.find((x) => x.id === c)?.label}`).join('; ') : '';
    case 'steps': return Object.values(r.value).join(' | ');
    case 'tokens': return resp.type === 'tokens' ? r.value.map((id) => resp.tokens.find((t) => t.id === id)?.label).join(' ') : '';
    case 'tf-reason': return `${r.truth === null ? '?' : r.truth ? L('True', 'Sant') : L('False', 'Usant')} — ${resp.type === 'tf-reason' ? resp.reasons.find((x) => x.id === r.reason)?.label ?? '' : ''}`;
    case 'pair': return resp.type === 'pair' ? `${resp.first.find((x) => x.id === r.first)?.label ?? '?'} / ${resp.second.find((x) => x.id === r.second)?.label ?? '?'}` : '';
  }
}

/** Probability of guessing correctly — used by the ability model. */
export function guessRate(q: Question | QuestionSnapshot): number {
  const r = q.response;
  if (r.type === 'choice') return 1 / Math.max(2, r.options.length);
  if (r.type === 'tf-reason') return 1 / (2 * Math.max(2, r.reasons.length));
  if (r.type === 'pair') return 1 / (r.first.length * r.second.length);
  if (r.type === 'multi') return 0.08;
  return 0;
}
